<script lang="ts">
  import { authUser, signOutUser } from '../lib/firebase';
  import { fontBase, FONT_LEVELS } from '../lib/settings';
  import Users from './Users.svelte';
  import Testers from './Testers.svelte';
  import Signup from './Signup.svelte';
  import DemoAccess from './DemoAccess.svelte';

  type Page = 'users' | 'testers' | 'signup' | 'demo';
  let page = $state<Page>('users');
  let collapsed = $state(false);

  const NAV: { id: Page; label: string; icon: string }[] = [
    { id: 'users',   label: 'Users',          icon: 'M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8' },
    { id: 'testers', label: 'Demo testers',   icon: 'M9 3H5a2 2 0 0 0-2 2v4M15 3h4a2 2 0 0 1 2 2v4M9 21H5a2 2 0 0 1-2-2v-4M15 21h4a2 2 0 0 0 2-2v-4M9 9h6v6H9z' },
    { id: 'signup',  label: 'Signup control', icon: 'M12 2a7 7 0 0 0-7 7v3H4a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1v-7a1 1 0 0 0-1-1h-1V9a7 7 0 0 0-7-7zM8 12V9a4 4 0 0 1 8 0v3' },
    { id: 'demo',    label: 'Demo access',    icon: 'M21 2l-2 2m-7.6 7.6a5.5 5.5 0 1 1-7.8 7.8 5.5 5.5 0 0 1 7.8-7.8zm0 0L19 4m-3 3l3 3' },
  ];

  function stepFont(dir: number) {
    const i = FONT_LEVELS.indexOf($fontBase);
    const ni = Math.min(FONT_LEVELS.length - 1, Math.max(0, (i < 0 ? 2 : i) + dir));
    fontBase.set(FONT_LEVELS[ni]);
  }
</script>

<div class="shell">
  <aside class="side" class:collapsed>
    <div class="brand">
      <img class="lotus" src="/wellness-blue.svg" alt="" aria-hidden="true" />
      {#if !collapsed}<div class="brandtext"><span class="mark">empowered</span><span class="chip">Admin</span></div>{/if}
      <button class="collapse" onclick={() => (collapsed = !collapsed)} title={collapsed ? 'Expand' : 'Collapse'} aria-label="Toggle sidebar">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d={collapsed ? 'M9 6l6 6-6 6' : 'M15 6l-6 6 6 6'} /></svg>
      </button>
    </div>

    <nav class="nav" aria-label="Admin sections">
      {#each NAV as n (n.id)}
        <button class:on={page === n.id} onclick={() => (page = n.id)} aria-current={page === n.id} title={n.label}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d={n.icon} /></svg>
          {#if !collapsed}<span>{n.label}</span>{/if}
        </button>
      {/each}
    </nav>

    <div class="foot">
      {#if !collapsed}
        <div class="textsize" title="Text size">
          <button onclick={() => stepFont(-1)} disabled={$fontBase <= FONT_LEVELS[0]} aria-label="Smaller text"><span style="font-size:11px">A</span></button>
          <button onclick={() => stepFont(1)} disabled={$fontBase >= FONT_LEVELS[FONT_LEVELS.length - 1]} aria-label="Larger text"><span style="font-size:16px">A</span></button>
        </div>
        <span class="email t-foot">{$authUser?.email}</span>
      {/if}
      <button class="out" onclick={() => signOutUser()} title="Sign out" aria-label="Sign out">
        {#if collapsed}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" /></svg>
        {:else}Sign out{/if}
      </button>
    </div>
  </aside>

  <main class="main">
    {#if page === 'users'}<Users />{:else if page === 'testers'}<Testers />{:else if page === 'demo'}<DemoAccess />{:else}<Signup />{/if}
  </main>
</div>

<style>
  .shell { display: grid; grid-template-columns: 232px 1fr; height: 100dvh; }
  .shell:has(.side.collapsed) { grid-template-columns: 64px 1fr; }
  .side { display: flex; flex-direction: column; gap: var(--space-4); padding: var(--space-4) var(--space-3); background: var(--surface); border-right: 1px solid var(--line); overflow: hidden; }

  .brand { display: flex; align-items: center; gap: var(--space-2); padding: 0 var(--space-1); }
  .lotus { width: 26px; height: 26px; object-fit: contain; flex: 0 0 auto; }
  .brandtext { display: flex; align-items: baseline; gap: var(--space-2); flex: 1; min-width: 0; }
  .mark { font-family: 'Audiowide', system-ui, sans-serif; color: var(--accent); font-size: var(--fs-heading); letter-spacing: 0.02em; }
  .chip { font-size: var(--fs-foot); font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; color: var(--text-3); }
  .collapse { display: grid; place-items: center; width: 26px; height: 26px; flex: 0 0 auto; border-radius: var(--radius-sm); color: var(--text-3); }
  .collapse:hover { color: var(--accent); background: var(--surface-2); }
  .collapse svg { width: 16px; height: 16px; }

  .nav { display: flex; flex-direction: column; gap: 2px; }
  .nav button { display: flex; align-items: center; gap: var(--space-3); width: 100%; padding: var(--space-2) var(--space-3); border-radius: var(--radius-md); color: var(--text-2); font-size: var(--fs-body); font-weight: 600; transition: color var(--ease), background var(--ease); }
  .side.collapsed .nav button { justify-content: center; }
  .nav button:hover { color: var(--text); background: var(--surface-2); }
  .nav button.on { color: var(--accent); background: var(--accent-soft); }
  .nav svg { width: 18px; height: 18px; flex: 0 0 auto; }

  .foot { margin-top: auto; display: flex; flex-direction: column; gap: var(--space-2); }
  .textsize { display: inline-flex; gap: 4px; }
  .textsize button { display: grid; place-items: center; width: 30px; height: 26px; border-radius: var(--radius-sm); color: var(--text-2); border: 1px solid var(--line-strong); }
  .textsize button:disabled { opacity: 0.4; cursor: default; }
  .textsize button:hover:not(:disabled) { color: var(--accent); border-color: var(--accent); }
  .email { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .out { display: flex; align-items: center; justify-content: center; gap: var(--space-2); padding: var(--space-2) var(--space-3); border-radius: var(--radius-pill); background: var(--surface-2); border: 1px solid var(--line-strong); color: var(--text-2); font-weight: 700; font-size: var(--fs-foot); }
  .out:hover { color: var(--danger); border-color: var(--danger); }
  .out svg { width: 16px; height: 16px; }

  .main { overflow-y: auto; min-width: 0; }
</style>
